import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useThemeContext } from "../context/ThemeContext";
import FeedbackWidget from "./FeedbackWidget";

const Navbar = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const { isDark, toggleTheme } = useThemeContext();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <nav className="bg-gray-900 border-b border-gray-800 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent whitespace-nowrap">
            TubeScript AI
          </span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/pricing"
            className="text-gray-400 hover:text-white transition"
          >
            Pricing
          </Link>
          {isAuthenticated && (
            <>
              <Link
                to="/dashboard"
                className="text-gray-400 hover:text-white transition"
              >
                Dashboard
              </Link>
              <Link
                to="/my-scripts"
                className="text-gray-400 hover:text-white transition"
              >
                My Scripts
              </Link>
              {user?.plan === "AGENCY" && (
                <Link
                  to="/team"
                  className="text-gray-400 hover:text-white transition"
                >
                  Team
                </Link>
              )}
              <button
                onClick={() => setFeedbackOpen(true)}
                className="text-gray-400 hover:text-white transition"
              >
                Feedback
              </button>
            </>
          )}
        </div>

        {/* Desktop Auth Buttons */}
        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-sm whitespace-nowrap">
                {user?.fullName}
              </span>
              <span
                className={`text-xs px-2 py-1 rounded-full font-semibold ${
                  user?.plan === "AGENCY"
                    ? "bg-yellow-500 text-black"
                    : user?.plan === "PRO"
                      ? "bg-purple-500 text-white"
                      : "bg-gray-700 text-gray-300"
                }`}
              >
                {user?.plan}
              </span>
              <button
                onClick={toggleTheme}
                className="text-gray-400 hover:text-white transition text-lg"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={logout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition"
              >
                Logout
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="nav-login text-gray-400 hover:text-white transition text-sm"
              >
                Login
              </button>
              <button
                onClick={toggleTheme}
                className="text-gray-400 hover:text-white transition text-lg"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/register")}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm transition"
              >
                Get Started
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden hamburger text-gray-400 hover:text-white text-2xl"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden mt-4 flex flex-col gap-4 pb-4 border-t border-gray-800 pt-4">
          <Link
            to="/pricing"
            onClick={() => setMenuOpen(false)}
            className="text-gray-400 hover:text-white transition"
          >
            Pricing
          </Link>
          {isAuthenticated && (
            <>
              <Link
                to="/dashboard"
                onClick={() => setMenuOpen(false)}
                className="text-gray-400 hover:text-white transition"
              >
                Dashboard
              </Link>
              <Link
                to="/my-scripts"
                onClick={() => setMenuOpen(false)}
                className="text-gray-400 hover:text-white transition"
              >
                My Scripts
              </Link>
              {user?.plan === "AGENCY" && (
                <Link
                  to="/team"
                  onClick={() => setMenuOpen(false)}
                  className="text-gray-400 hover:text-white transition"
                >
                  Team
                </Link>
              )}
              <button
                onClick={() => {
                  setFeedbackOpen(true);
                  setMenuOpen(false);
                }}
                className="text-gray-400 hover:text-white transition text-left"
              >
                Feedback
              </button>
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-sm">{user?.fullName}</span>
                <span
                  className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    user?.plan === "AGENCY"
                      ? "bg-yellow-500 text-black"
                      : user?.plan === "PRO"
                        ? "bg-purple-500 text-white"
                        : "bg-gray-700 text-gray-300"
                  }`}
                >
                  {user?.plan}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleTheme}
                  className="text-gray-400 hover:text-white transition text-lg"
                >
                  {isDark ? "☀️" : "🌙"}
                </button>
                <button
                  onClick={logout}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition"
                >
                  Logout
                </button>
              </div>
            </>
          )}
          {!isAuthenticated && (
            <div className="flex flex-col gap-3">
              <button
                onClick={toggleTheme}
                className="text-gray-400 hover:text-white transition text-lg text-left"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => {
                  navigate("/login");
                  setMenuOpen(false);
                }}
                className="nav-login text-gray-400 hover:text-white transition text-sm text-left"
              >
                Login
              </button>
              <button
                onClick={() => {
                  navigate("/register");
                  setMenuOpen(false);
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm transition"
              >
                Get Started
              </button>
            </div>
          )}
        </div>
      )}

      {/* Feedback window, opened from the menu */}
      <FeedbackWidget
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
    </nav>
  );
};

export default Navbar;
